import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth } from "../firebase/config";
import { getUserData } from "../services/authService";
import { getBooksByLanguage } from "../services/bookService";

// Descriptions des livres (par langue)
const booksDescriptions = {
  fr: {
    1: "Ce livre explore une vision renouvelée du management de projet en articulant trois dimensions indissociables : l'humain, la science et les outils.",
    2: "« L'État d'Esprit du Leader » explore le rôle déterminant du mindset et de la posture intérieure du leader.",
    3: "La réussite d'un projet ne dépend pas uniquement des méthodes, mais avant tout des dynamiques humaines.",
    4: "Ce livre propose le Modèle Symphonique, une méthodologie innovante inspirée de la musique.",
    5: "« Planification Harmonieuse » considère le projet comme une véritable symphonie.",
    6: "Une approche renouvelée de la gestion des risques à travers l'univers musical.",
    7: "Une invitation à la transformation intérieure à travers 24 principes puissants.",
    8: "Quand tu comprends le pouvoir de la musique sur l'esprit, tu n'écoutes plus n'importe quoi."
  },
  en: {
    1: "This book explores a renewed vision of project management by articulating three inseparable dimensions: people, science, and tools.",
    2: "« The Leader's Mindset » explores the determining role of mindset and the leader's inner posture.",
    3: "A project's success depends not only on methods, but above all on human dynamics.",
    4: "This book presents the Symphonic Model, an innovative methodology inspired by music.",
    5: "« Harmonious Planning » considers the project as a true symphony.",
    6: "A renewed approach to risk management through the musical universe.",
    7: "An invitation to inner transformation through 24 powerful principles.",
    8: "When you understand the power of music on the mind, you don't listen to just anything anymore."
  }
};

function Library() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [books, setBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBook, setSelectedBook] = useState(null);

  // Langue courante
  const lang = i18n.language || 'fr';

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const loadData = async () => {
      // Charger les données utilisateur
      const userResult = await getUserData(user.uid);
      if (userResult.success) {
        setUserData(userResult.data);
      }

      // Charger les livres selon la langue sélectionnée
      const booksResult = await getBooksByLanguage(lang);
      if (booksResult.success) {
        const enrichedBooks = booksResult.books.map((book) => ({
          ...book,
          description:
            booksDescriptions[lang]?.[book.bookNumber] ||
            book.description ||
            "",
        }));
        setBooks(enrichedBooks);
      }

      setLoading(false);
    };

    loadData();
  }, [user, navigate, lang]);

  const filteredBooks = books
    .filter((book) => {
      const matchesSearch =
        book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "all" || book.category === selectedCategory;

      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const order = lang === 'fr' 
        ? {
            Leadership: 1,
            "Management de projet": 2,
            "Développement personnel": 3,
          }
        : {
            Leadership: 1,
            "Project Management": 2,
            "Personal Development": 3,
          };

      return (
        (order[a.category] || 99) - (order[b.category] || 99) ||
        (a.bookNumber || 0) - (b.bookNumber || 0)
      );
    });

  const openBook = (book) => {
    // Vérifier si l'utilisateur a l'abonnement Gold
    if (userData?.subscriptionType !== "gold") {
      alert(t('libraryLocked') + "\n\n" + t('upgradeToGoldToRead'));
      navigate('/pricing');
      return;
    }

    if (!book.pdfUrl) return;
    setSelectedBook(book);
  };

  const isGoldUser = userData?.subscriptionType === "gold";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600 text-lg">{t("loadingEllipsis")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Viewer PDF */}
      {selectedBook && isGoldUser && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col">
          <div className="bg-white p-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">{selectedBook.title}</h2>
            <button onClick={() => setSelectedBook(null)} className="btn-secondary">
              ✕ {t("close")}
            </button>
          </div>
          <iframe
            src={`${selectedBook.pdfUrl}#toolbar=0`}
            title={selectedBook.title}
            className="flex-1 w-full"
          />
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-primary-600 font-semibold mb-4"
          >
            {t("backDashboard")}
          </button>
          <h1 className="text-3xl font-bold">
            {t("repriseControl")}
          </h1>
          <p className="text-gray-600 mt-2">
            {t("exclusiveCollection")}
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Bannière pour utilisateurs non-Gold */}
        {!isGoldUser && (
          <div className="card mb-8 bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-400">
            <div className="flex items-center gap-4">
              <div className="text-5xl">🔒</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('upgradeToGoldAccess')}
                </h3>
                <p className="text-gray-700 mb-4">
                  {t('goldMembersOnlyReading')}
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="btn-primary"
                >
                  ⭐ {t('upgradeGold')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="card mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              className="input-field flex-1"
              placeholder={`🔍 ${t("searchBook")}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <select
              className="input-field md:w-64"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">📚 {t("allCategories")}</option>
              <option value={lang === 'fr' ? "Management de projet" : "Project Management"}>
                📊 {t("projectManagement")}
              </option>
              <option value="Leadership">👤 {t("leadership")}</option>
              <option value={lang === 'fr' ? "Développement personnel" : "Personal Development"}>
                🌱 {t("personalDevelopment")}
              </option>
            </select>
          </div>

          <p className="mt-4 text-sm text-gray-600">
            {filteredBooks.length} {t("booksFound")}
          </p>
        </div>

        {/* Liste */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredBooks.map((book) => (
            <div key={book.id} className="card hover:shadow-xl transition">
              <div className="flex gap-4">
                <div className="text-5xl">{book.icon}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-1">{book.title}</h3>
                  <p className="text-sm text-primary-600 font-semibold">
                    {book.author}
                  </p>
                  <span className="inline-block mt-2 mb-3 text-xs bg-primary-100 text-primary-800 px-3 py-1 rounded-full">
                    {book.category}
                  </span>
                  <p className="text-sm text-gray-600 mb-4">
                    {book.description.slice(0, 180)}…
                  </p>

                  <button
                    onClick={() => openBook(book)}
                    disabled={!book.pdfUrl}
                    className={`w-full ${
                      !book.pdfUrl 
                        ? "btn-secondary cursor-not-allowed"
                        : !isGoldUser
                        ? "bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition"
                        : "btn-primary"
                    }`}
                  >
                    {!book.pdfUrl 
                      ? `⏳ ${t("comingSoon")}`
                      : !isGoldUser
                      ? `🔒 ${t('upgradeToRead')}`
                      : `📖 ${t("readNow")}`
                    }
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="mt-10 bg-blue-50 border border-blue-200 rounded-lg p-6 text-sm text-blue-800">
          <strong>📝 Note :</strong> {t("libraryNote")}
        </div>
      </main>
    </div>
  );
}

export default Library;
